import { usePathname, useRouter } from 'next/navigation';

import { FC, ReactNode, useMemo } from 'react';
import { toast } from 'react-toastify';

import classNames from 'classnames';

import {
  ExternalLink,
  HorizontalSeparator,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalDescription,
  ModalHeading,
  Spinner,
} from '@uikit';

import { useLoginViaTonWalletMutation } from '@query';

import { DeserializedUser } from '@api';

import { Discord } from '@uikit/icons/Discord';
import { EvmDark } from '@uikit/icons/EvmDark';
import { Google } from '@uikit/icons/Google';
import { Telegram } from '@uikit/icons/Telegram';
import { XRoundedOutline } from '@uikit/icons/XRoundedOutline';

import { useAuthUsingTestUser } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import useGlobalState from '@shared/common/providers/GlobalStateProvider';
import { getProcessEnvGlobalConfig } from '@shared/config/global';
import { GENERAL_URLS, nextProjectUrl } from '@shared/constants/urls';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import { getIsPasskeysConnected, useLogin } from '@shared/hooks/auth';
import { isRedirectOnLoginDisabled } from '@shared/hooks/useDisableRedirectOnLogin';
import { useFeatureToggles } from '@shared/hooks/useFeatureToggles';

import ConnectTonWalletButtonStore from './ConnectTonWalletButtonStore';
import PasswordlessAuth from './PasswordlessAuth/PasswordlessAuth';
import DiscordAuthButton from './SocialAuthButton/DiscordAuthButton';
import GoogleAuthButton from './SocialAuthButton/GoogleAuthButton';
import TelegramAuthButton from './SocialAuthButton/TelegramAuthButton';
import XAuthButton from './SocialAuthButton/XAuthButton';

export interface SocialAuthButtonProps {
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
}

const useSocialsLoginOptions = () => {
  const featureToggles = useFeatureToggles();

  return useMemo(() => {
    return [
      {
        id: 'google',
        bgClassName: 'bg-[#E7F2FF]',
        Icon: Google,
        Component: GoogleAuthButton,
        enabled: featureToggles['oauth2.0'],
      },
      {
        id: 'x',
        bgClassName: 'bg-neutral-600',
        Icon: XRoundedOutline,
        Component: XAuthButton,
        enabled: featureToggles.xAuth,
      },
      {
        id: 'discord',
        bgClassName: 'bg-[#4056F7]',
        Icon: Discord,
        Component: DiscordAuthButton,
        enabled: featureToggles.discordAuth,
      },
      {
        id: 'telegram',
        bgClassName: 'bg-[#0E98DE]',
        Icon: Telegram,
        Component: TelegramAuthButton,
        enabled: featureToggles.telegramLogin,
      },
    ].filter((option) => option.enabled);
  }, [featureToggles]);
};

const isDev = getProcessEnvGlobalConfig('isDev');

export interface AuthModalProperties {
  pathExcludedFromRedirect?: Array<string>;
}

const AuthModal: FC = ({ pathExcludedFromRedirect }: AuthModalProperties = {}) => {
  const featureToggles = useFeatureToggles();
  const authModal = useAuthModal();
  const router = useRouter();
  const { login: loginTest, isLoading: isLoggingInAsTestUser } = useAuthUsingTestUser();
  const { value: wantsInMagicBoost } = useGlobalState('wantsInMagicBoost');
  const loginViaTonWalletMutation = useLoginViaTonWalletMutation();
  const {
    login: walletConnectOrPasskeyLogin,
    isLogging: isLoggingInWithWallet,
    isLoggingInWithPasskey,
    isLoggingInWithWalletConnect,
  } = useLogin();

  const pathname = usePathname();

  // getting once on first render, because value can change when user connect wallet
  const isPasskeyConnected = useMemo(getIsPasskeysConnected, []);

  const socialsLoginOptions = useSocialsLoginOptions();

  const isLoggingIn =
    isLoggingInWithWallet || isLoggingInAsTestUser || loginViaTonWalletMutation.isPending;

  const handleRedirect = (user: DeserializedUser, searchParams: URLSearchParams) => {
    // NOTE: workaround to prevent redirection to profile page when user on below pages
    const shouldRedirect =
      !isRedirectOnLoginDisabled(pathname) &&
      !pathExcludedFromRedirect?.includes(pathname.toLowerCase()) &&
      !pathname.toLowerCase().startsWith('/hot-offers');

    if (shouldRedirect) {
      const name = user.attributes?.name;
      const email = user.attributes?.email;

      let redirectUrl = '';

      if (!name || !email) {
        const loginMethods = searchParams.get('utm_register_method');
        redirectUrl = nextProjectUrl.store(
          `/profile/magic-id?fillProfile=true&from=${window.location.href}${
            loginMethods ? `&utm_register_method=${loginMethods}` : ''
          }`,
        );
      } else {
        if (wantsInMagicBoost) {
          redirectUrl = '/magic-boost';
        }

        const authRedirectPathname = searchParams.get('authRedirectPathname');

        if (authRedirectPathname) {
          redirectUrl = authRedirectPathname;
        }
      }

      if (redirectUrl) {
        router.push(redirectUrl);
      }
    }
  };

  const login = async (type: 'Passkey' | 'WalletConnect2') => {
    return walletConnectOrPasskeyLogin({
      walletType: type,
      signMessage: 'Confirm authorization in Magic Store with your wallet',
      shouldReconnect: true,
      onLoginCallback: (userInfo) => {
        const searchParams = new URLSearchParams(window.location.search);

        if (userInfo.isNewUser) {
          searchParams.append('utm_register_method', type);
        }

        handleRedirect(userInfo.user, searchParams);
        localStorage.removeItem('currentSessionChatId');
        authModal.close();
      },
    });
  };

  const loginAsTestUser = async () => {
    if (!isDev) {
      toast('This option should be only available in the development environment');

      return;
    }

    try {
      await loginTest();
      localStorage.removeItem('currentSessionChatId');
      authModal.close();

      if (wantsInMagicBoost) {
        router.push('/magic-boost');
      }
    } catch (error) {
      displayErrorToast({ error });
    }
  };

  const showSocialsLoginOptions = !!socialsLoginOptions.length;
  const showCreateWalletButton = featureToggles.passkeyAuth && !isPasskeyConnected;

  return (
    <Modal {...authModal.props}>
      <ModalContent
        className={classNames(
          'rounded-xl w-full max-w-[30.625rem] border-none overflow-hidden gap-0 px-4 py-6 sm:pb-10 sm:px-6',
        )}
      >
        <ModalCloseButton className="!m-2" />

        <div className="sm:px-10">
          <ModalHeading className="mb-3">
            Welcome to Magic Store <br />
            The Web3 App Store
          </ModalHeading>

          <ModalDescription className="text-neutral-600 text-xs leading-4 font-medium mb-6">
            Sign up or log in to discover community-vetted apps and games, earn rewards, and be part
            of a community actively shaping the Web3 future!
          </ModalDescription>

          <PasswordlessAuth disabled={isLoggingIn} className="w-full" />

          <HorizontalSeparator className="my-6">Or</HorizontalSeparator>

          <div className="flex max-sm:flex-col sm:flex-wrap gap-2">
            <button
              disabled={isLoggingIn}
              onClick={() => login('WalletConnect2')}
              className="relative button button-solid-neutral-600 button-lg gap-2 rounded-lg px-2 flex-1"
            >
              {isLoggingInWithWalletConnect && <Spinner size="sm" color="disabled" />}
              {!isLoggingInWithWalletConnect && (
                <>
                  <EvmDark
                    className={classNames('icon max-sm:absolute left-2', {
                      'grayscale opacity-20': isLoggingIn,
                    })}
                  />
                  EVM Wallet
                </>
              )}
            </button>

            {featureToggles.tonConnectLogin && (
              <ConnectTonWalletButtonStore
                loginViaTonWalletMutation={loginViaTonWalletMutation}
                disabled={isLoggingIn}
              />
            )}

            {showCreateWalletButton && (
              <button
                disabled={isLoggingIn}
                onClick={() => login('Passkey')}
                className="button button-solid-primary-200 text-primary button-lg rounded-lg px-2 w-full"
              >
                {isLoggingInWithPasskey && <Spinner size="sm" color="disabled" />}
                {!isLoggingInWithPasskey && 'Create Wallet'}
              </button>
            )}

            {isDev && (
              <button
                disabled={isLoggingIn}
                onClick={() => loginAsTestUser()}
                className="button button-solid-neutral-600 button-lg rounded-lg px-2 w-full"
              >
                {isLoggingInAsTestUser && <Spinner size="sm" color="disabled" />}
                {!isLoggingInAsTestUser && 'Login as Test User'}
              </button>
            )}
          </div>

          {showSocialsLoginOptions && (
            <>
              <HorizontalSeparator className="my-4">Or Sign in by:</HorizontalSeparator>

              <div
                className={classNames('flex gap-3 justify-center', {
                  'grayscale opacity-40 cursor-not-allowed': isLoggingIn,
                })}
              >
                {socialsLoginOptions.map(({ Component, Icon, bgClassName, id }) => {
                  return (
                    <Component
                      key={id}
                      disabled={isLoggingIn}
                      className={classNames(
                        'rounded-full size-8 flex items-center transition-opacity hover:opacity-50 active:opacity-100 text-neutral-50 justify-center',
                        {
                          'pointer-events-none': isLoggingIn,
                        },
                        bgClassName,
                      )}
                    >
                      <Icon className="icon" />
                    </Component>
                  );
                })}
              </div>
            </>
          )}

          <p className="text-center text-sm mt-4 text-[#918FA0]">
            By continuing you agree to Magic Square's
            <br />
            <ExternalLink className="text-primary-600" href={GENERAL_URLS.privacyPolicy}>
              Privacy Policy
            </ExternalLink>{' '}
            and{' '}
            <ExternalLink className="text-primary-600" href={GENERAL_URLS.termsAndConditions}>
              Terms and Conditions
            </ExternalLink>
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default AuthModal;
